// Fallback for using MaterialIcons on Android and web.

import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import { SymbolWeight } from "expo-symbols";
import { ComponentProps } from "react";
import { OpaqueColorValue, type StyleProp, type TextStyle } from "react-native";

type IconSymbolName =
  | "house.fill"
  | "globe"
  | "paperplane.fill"
  | "chevron.left.forwardslash.chevron.right"
  | "chevron.right"
  | "chevron.up"
  | "chevron.down"
  | "calendar"
  | "calendar.circle.fill"
  | "clock"
  | "ellipsis.vertical"
  | "person.fill"
  | "arrow.right.to.line"
  | "gearshape"
  | "attach.file"
  | "paperclip"
  | "location"
  | "location.fill"
  | "location.slash"
  | "car"
  | "bell"
  | "checkmark.square.fill"
  | "square"
  | "group"
  | "chevron.left"
  | "xmark.circle.fill"
  | "padlock"
  | "questionmark.circle"
  | "link";

/**
 * Add your SF Symbols to Material Icons mappings here.
 * - see Material Icons in the [Icons Directory](https://icons.expo.fyi).
 * - see SF Symbols in the [SF Symbols](https://developer.apple.com/sf-symbols/) app.
 */
const MAPPING: Record<
  IconSymbolName,
  ComponentProps<typeof MaterialIcons>["name"]
> = {
  "house.fill": "home",
  globe: "public",
  "paperplane.fill": "send",
  "chevron.left.forwardslash.chevron.right": "code",
  "chevron.right": "chevron-right",
  "chevron.up": "expand-less",
  "chevron.down": "expand-more",
  calendar: "calendar-today",
  "calendar.circle.fill": "event",
  clock: "access-time",
  "ellipsis.vertical": "more-vert",
  "arrow.right.to.line": "logout",
  "person.fill": "person",
  gearshape: "settings",
  "attach.file": "attach-file",
  paperclip: "attach-file",
  location: "location-on",
  "location.fill": "my-location",
  "location.slash": "location-off",
  car: "directions-car",
  bell: "notifications",
  "checkmark.square.fill": "check-box",
  square: "check-box-outline-blank",
  group: "groups",
  "chevron.left": "arrow-back-ios",
  "xmark.circle.fill": "cancel",
  padlock: "lock",
  "questionmark.circle": "help",
  link: "link",
};

/**
 * An icon component that uses native SF Symbols on iOS, and Material Icons on Android and web.
 * This ensures a consistent look across platforms, and optimal resource usage.
 * Icon `name`s are based on SF Symbols and require manual mapping to Material Icons.
 */
export function IconSymbol({
  name,
  size = 24,
  color,
  style,
}: {
  name: IconSymbolName;
  size?: number;
  color: string | OpaqueColorValue;
  style?: StyleProp<TextStyle>;
  weight?: SymbolWeight;
}) {
  return (
    <MaterialIcons
      color={color}
      size={size}
      name={MAPPING[name]}
      style={style}
    />
  );
}
